/**
 * The global-attribute counterpart to {@link useAttributeNameConflict}: same
 * contract, wider scope — a global is checked against every space's locals, not
 * just one space's. Moving either behind an API is a change to the hook alone;
 * callers gate their commit on `isChecking` (constant `false` today).
 */
import { useMemo } from "react";
import {
  findGlobalAttributeNameConflict,
  localAttributesAcrossSpaces,
} from "#common/attributeNameConflicts";
import type { AttributeNameConflictResult } from "#common/hooks/useAttributeNameConflict";
import type { GlobalAttribute, Space } from "#common/types/vault";

export type UseGlobalAttributeNameConflictInput = {
  name: string;
  /** Omit when editing an existing global: its key is frozen and cannot collide. */
  slug?: string;
  globalAttributes: readonly GlobalAttribute[];
  /** Every space, so no space's locals can be shadowed by the new global. */
  spaces: readonly Space[];
  ignoreGlobalKey?: string;
  /** The local being promoted to global, which does not clash with itself. */
  ignoreLocal?: { spaceId: string; localId: string };
};

export function useGlobalAttributeNameConflict({
  name,
  slug,
  globalAttributes,
  spaces,
  ignoreGlobalKey,
  ignoreLocal,
}: UseGlobalAttributeNameConflictInput): AttributeNameConflictResult {
  const localAttributes = useMemo(() => localAttributesAcrossSpaces(spaces), [spaces]);
  // Depend on the ids rather than the object so a fresh literal per render doesn't
  // defeat the memo.
  const ignoreSpaceId = ignoreLocal?.spaceId;
  const ignoreLocalId = ignoreLocal?.localId;

  const conflict = useMemo(
    () =>
      findGlobalAttributeNameConflict({
        name,
        slug,
        globalAttributes,
        localAttributes,
        ignoreGlobalKey,
        ignoreLocal:
          ignoreSpaceId && ignoreLocalId
            ? { spaceId: ignoreSpaceId, localId: ignoreLocalId }
            : undefined,
      }),
    [
      name,
      slug,
      globalAttributes,
      localAttributes,
      ignoreGlobalKey,
      ignoreSpaceId,
      ignoreLocalId,
    ],
  );

  return { conflict, isChecking: false };
}
